import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiBox, MiList, MiMenu, MiModalConfirm } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  ICONS_WEB,
  MODAL_TITLES,
  PAGINATION,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { formatDate } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box } from "@mui/material";
import { StatusChip } from "../../../../components/StatusChip/StatusChip";
import { ImportPlanDrawer } from "./ImportPlanDrawer/ImportPlanDrawer";
import { usePlanSeriesCommunitiesPlansTabViewModel } from "./usePlanSeriesCommunitiesPlansTabViewModel";

export function PlanSeriesCommunitiesPlansTab({
  stateManager,
  openDrawer,
  setOpenDrawer,
  accountDetail,
}) {
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedSpecPlan, setSelectedSpecPlan] = useState({});

  const {
    params: { accountId, tab, seriesOrCommunityId, seriesOrCommunityTab },
    navigate,
  } = useRouter();
  const {
    specPlans,
    refresh,
    getSpecPlans,
    setSpecPlans,
    handleRefresh,
    handleDeleteSpecPlan,
    recentSpecPlans,
    setRecentSpecPlans,
    getRecentSpecPlans,
  } = usePlanSeriesCommunitiesPlansTabViewModel({
    stateManager,
    accountId,
    seriesOrCommunityId,
    seriesOrCommunityTab,
    tab,
  });
  const { classes, theme } = useStyles();

  useEffect(() => {
    handleRefresh();
  }, [seriesOrCommunityTab]);

  const sections = [
    {
      fieldset: [
        {
          label: "Name",
          field: "name",
        },
        {
          label: "Status",
          field: "planStatus",
          renderValue: ({ planStatus }) => <StatusChip status={planStatus} />,
        },
        {
          label: "Builder",
          field: "builderName",
        },
        {
          label: "Series",
          field: "series",
        },
        {
          label: "Created Date",
          field: "createdOn",
          renderValue: ({ createdOn }) =>
            formatDate(createdOn, "ddd, MMMM D, YYYY"),
        },
      ],
    },
  ];

  const isLoading = stateManager.isBusy();

  const renderItemActions = (item) => (
    <Box onClick={(e) => e.stopPropagation()}>
      <MiMenu
        data={[
          {
            label: BUTTON_LABELS.EDIT,
            icon: mdi[ICONS_WEB.PENCIL_OUTLINE],
            action: () => handleNavigate(item),
          },
          {
            label: BUTTON_LABELS.DELETE,
            icon: mdi[ICONS_WEB.TRASH_CAN],
            action: () => handleOpenDeleteModal(item),
            color: theme.palette.red.main,
          },
        ]}
      />
    </Box>
  );

  const handleNavigate = (item) => {
    if (item.isImported) {
      navigate(
        `/estimating/old-bids/${accountId}/${tab}/${seriesOrCommunityId}/bid-import-review/${item.id}`
      );
    } else {
      navigate(
        `/estimating/old-bids/${accountId}/${tab}/${seriesOrCommunityId}/add-bid/${item.id}`
      );
    }
  };

  const handleOpenDeleteModal = (item) => {
    setSelectedSpecPlan(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    handleDeleteSpecPlan(selectedSpecPlan);
    setOpenDeleteModal(false);
  };

  return (
    <Box className={classes.root}>
      <MiBox className={classes.container} height="auto">
        <MiList
          viewType="card"
          showRecent
          cardProps={{
            sections,
            renderItemActions,
            onClick: (item) => handleNavigate(item),
          }}
          loading={isLoading}
          data={specPlans}
          setData={setSpecPlans}
          dataProvider={getSpecPlans}
          recentData={recentSpecPlans}
          setRecentData={setRecentSpecPlans}
          recentDataProvider={getRecentSpecPlans}
          refresh={refresh}
          paginationMode={PAGINATION.SERVER}
          emptyListProps={{
            text: "No Bids found",
          }}
          data-testid="series-communities-plan-list"
        />
      </MiBox>
      <ImportPlanDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        accountDetail={accountDetail}
        stateManager={stateManager}
      />
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        textPositive={BUTTON_LABELS.DELETE}
        buttonPositiveColor={BUTTON_COLORS.RED}
        titleIcon={{
          path: mdi[ICONS_WEB.TRASH_CAN],
          color: theme.palette.red.main,
        }}
        handleNegative={() => setOpenDeleteModal(false)}
        handlePositive={handleConfirmDeleteModal}
        description={`You are about to delete ${selectedSpecPlan.name}. Are you sure you would like to continue?`}
      />
    </Box>
  );
}

const useStyles = makeStyles()(() => ({
  root: {
    display: "flex",
    overflow: "auto",
    height: "100%",
  },
  container: {
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
}));
